import {
  Box,
  Button,
  Icon,
  InlineStack,
  Link,
  TableTd,
  TableTr,
  Text,
  TextField,
  TextLabel,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { useController, useFormContext, useWatch, type UseFormReturn } from '@/components/form';
import type { AttributesFormInput } from '../utils/attributes-form';
import { canIdentifyBy, isAttributeImmutableIdentifier, type IdentifiersConfig } from '../utils/identifiers';
import {
  CREATED_IN_CIO_AT,
  IdentifierKey,
  IMMUTABLE_CUSTOMER_ATTRIBUTES,
  PERMANENT_ATTRIBUTES,
} from '../utils/attribute-keys';
import { AttributeTypeLabel } from './attribute-type-label';
import { ServerErrorMessage } from './server-error-message';

const KEY_COLUMN_STYLE = { width: 256 };

type AttributeEditorRowProps = {
  /** The row's index in the form's `attributes` array. */
  index: number;
  /** The field array's stable id for this row, which the value input's DOM id derives from. */
  fieldId: string;
  identifiers: IdentifiersConfig | null | undefined;
  isEmailIdentified: boolean;
  isSaving: boolean;
  /** Called after the name changes, so the caller can update the hidden names. */
  onKeyChange: (index: number) => void;
  /** Called after any edit to the row. */
  onChange: () => void;
  onRemove: (index: number) => void;
};

/** What a row's name allows, derived from the name and the workspace's identifier config. */
type RowTraits = {
  /** The name is fixed: it cannot be renamed or removed. */
  isPermanent: boolean;
  isKeyEditable: boolean;
  isPhoneIdentifier: boolean;
};

function rowTraits(key: string, originalKey: string, identifiers: IdentifiersConfig | null | undefined): RowTraits {
  const isKeyChanged = key !== originalKey;
  const isCreated = originalKey === '';
  const isPhoneIdentifier = key === IdentifierKey.Phone && canIdentifyBy(IdentifierKey.Phone, identifiers);
  const isImmutable =
    key === CREATED_IN_CIO_AT ||
    key === IdentifierKey.CioId ||
    IMMUTABLE_CUSTOMER_ATTRIBUTES.includes(key) ||
    isAttributeImmutableIdentifier(key, identifiers);
  // A promoted phone identifier is as permanent as id/email: its name can't change, its value can.
  const isPermanent = isImmutable || ((PERMANENT_ATTRIBUTES.includes(key) || isPhoneIdentifier) && !isKeyChanged);
  return { isPermanent, isKeyEditable: isCreated || !isPermanent, isPhoneIdentifier };
}

function valuePlaceholder(key: string, isPhoneIdentifier: boolean): string {
  if (key === IdentifierKey.Email) {
    return 'email@domain.com';
  }
  if (isPhoneIdentifier) {
    return '+12125552368';
  }
  return 'Value';
}

type KeyHelpProps = {
  isEmailValidated: boolean;
  isPhoneIdentifier: boolean;
};

/** The help tooltip beside an identifier's fixed name, explaining the format the value must take. */
function KeyHelp({ isEmailValidated, isPhoneIdentifier }: KeyHelpProps) {
  if (isEmailValidated) {
    return (
      <Tooltip>
        <Icon size="sm" name="help" color="subtle" />
        <TooltipContent>
          Email is a unique key and we only accept valid email address format.{' '}
          <Link
            href="https://docs.customer.io/manually-adding-or-updating-people#email-address-validation"
            variant="secondary"
            color="inherit"
            isExternal
          >
            Read more about email validation
          </Link>
          .
        </TooltipContent>
      </Tooltip>
    );
  }
  if (isPhoneIdentifier) {
    return (
      <Tooltip>
        <Icon size="sm" name="help" color="subtle" />
        <TooltipContent>
          Phone is a unique key and we only accept valid phone numbers in E.164 format (for example, +12125552368).
        </TooltipContent>
      </Tooltip>
    );
  }
  return null;
}

type FixedKeyLabelProps = {
  attributeKey: string;
  valueInputId: string;
  identifiers: IdentifiersConfig | null | undefined;
  isEmailIdentified: boolean;
  isPhoneIdentifier: boolean;
};

/** A name that cannot change, labelling the value input beside it. */
function FixedKeyLabel({
  attributeKey,
  valueInputId,
  identifiers,
  isEmailIdentified,
  isPhoneIdentifier,
}: FixedKeyLabelProps) {
  return (
    <TextLabel htmlFor={valueInputId} display="flex" alignItems="center" gap="050">
      <Text as="span" data-test-editor-key-label>
        {attributeKey}
      </Text>
      <AttributeTypeLabel attribute={attributeKey} identifiers={identifiers} />
      <KeyHelp
        isEmailValidated={attributeKey === IdentifierKey.Email && isEmailIdentified}
        isPhoneIdentifier={isPhoneIdentifier}
      />
    </TextLabel>
  );
}

/** The Pluma bindings of one field: value in, `onInput` out, plus the first error message. */
type RowField = {
  name: string;
  value: string;
  onInput: (event: { target: { value: string } }) => void;
  onBlur: () => void;
  error: string | undefined;
};

type RowFieldName = `attributes.${number}.key` | `attributes.${number}.value`;

/**
 * Binds a row field through `useController`. The shared Pluma bridges write with
 * `setValue`, which for a path inside a field array re-emits the whole array and
 * makes `useFieldArray` regenerate its ids — remounting every row mid-keystroke.
 * The controller's `onChange` updates the value without touching the array.
 */
function useRowField(control: UseFormReturn<AttributesFormInput>['control'], name: RowFieldName): RowField {
  const { field, fieldState } = useController({ control, name });
  return {
    name,
    value: field.value,
    onInput: (event) => field.onChange(event.target.value),
    onBlur: field.onBlur,
    error: fieldState.error?.message,
  };
}

type KeyFieldProps = {
  field: RowField;
  isSaving: boolean;
  onInput: RowField['onInput'];
};

function KeyField({ field, isSaving, onInput }: KeyFieldProps) {
  return (
    <TextField
      {...field}
      onInput={onInput}
      ariaLabel="Attribute name"
      placeholder="Attribute name"
      isDisabled={isSaving}
      isInvalid={field.error != null}
      data-test-editor-key-input={field.value}
    />
  );
}

/**
 * One attribute of a profile: a name that is editable for custom attributes
 * and fixed for identifiers and reserved names, and an editable value.
 */
export function AttributeEditorRow({
  index,
  fieldId,
  identifiers,
  isEmailIdentified,
  isSaving,
  onKeyChange,
  onChange,
  onRemove,
}: AttributeEditorRowProps) {
  const { control } = useFormContext<AttributesFormInput>();
  const originalKey = useWatch({ control, name: `attributes.${index}.originalKey` });
  const keyField = useRowField(control, `attributes.${index}.key`);
  const valueField = useRowField(control, `attributes.${index}.value`);
  const key = keyField.value;
  const { isPermanent, isKeyEditable, isPhoneIdentifier } = rowTraits(key, originalKey, identifiers);
  const valueInputId = `attribute-value-${fieldId}`;

  return (
    <TableTr data-test="attribute-editor">
      <TableTd
        style={KEY_COLUMN_STYLE}
        overflowWrap="words"
        verticalAlign={isKeyEditable ? 'top' : undefined}
        data-test-attribute-key={key}
      >
        {isKeyEditable ? (
          <KeyField
            field={keyField}
            isSaving={isSaving}
            onInput={(event) => {
              keyField.onInput(event);
              onKeyChange(index);
              onChange();
            }}
          />
        ) : (
          <FixedKeyLabel
            attributeKey={key}
            valueInputId={valueInputId}
            identifiers={identifiers}
            isEmailIdentified={isEmailIdentified}
            isPhoneIdentifier={isPhoneIdentifier}
          />
        )}
      </TableTd>
      <TableTd verticalAlign="top">
        <InlineStack alignBlock="center" gap="050">
          <Box flex={1}>
            <TextField
              {...valueField}
              onInput={(event) => {
                valueField.onInput(event);
                onChange();
              }}
              error={valueField.error != null ? <ServerErrorMessage message={valueField.error} /> : undefined}
              id={valueInputId}
              className="fs-mask"
              ariaLabel="Attribute value"
              placeholder={valuePlaceholder(key, isPhoneIdentifier)}
              isDisabled={isSaving}
              isInvalid={valueField.error != null}
              data-test-editor-value-input={key}
            />
          </Box>
          {!isPermanent ? (
            <Button
              icon="delete-trash"
              isDisabled={isSaving}
              isIconOnly
              size="sm"
              variant="tertiary"
              onClick={() => onRemove(index)}
              aria-label={`Remove ${key} attribute`}
              data-test-editor-remove={key}
            />
          ) : null}
        </InlineStack>
      </TableTd>
    </TableTr>
  );
}
